var _excluded = ["children", "leftIcon", "rightIcon", "label", "disabled", "isLoading", "loadingText"];

function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

import React, { useEffect } from 'react';
import { AccessibleIcon } from '@radix-ui/react-accessible-icon';
import { forwardRef, styled } from '@grupoboticario/flora-react-system';
import { Spinner } from '@grupoboticario/flora-react-spinner';
// Não existe um token em design para essa cor. Por este motivo, foi criado um token temporário, que será substituído quando o design estiver pronto.
var actionNavigationWarningHighlight = '#C81414';
var StyledButton = styled('button', {
  display: 'inline-flex',
  pointerEvents: 'auto',
  alignItems: 'center',
  justifyContent: 'center',
  border: 'none',
  appearance: 'none',
  userSelect: 'none',
  outline: 'none',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  verticalAlign: 'middle',
  lineHeight: '16px',
  transition: "\n    background 150ms ease,\n      box-shadow 150ms ease-in-out,\n      color 150ms ease\n  ",
  '&:disabled, &[disabled], &[aria-disabled=true]': {
    background: '$disabledBackground',
    color: '$disabledContent',
    pointerEvents: 'none',
    cursor: 'auto'
  },
  '&::before, &::after': {
    boxSizing: 'border-box'
  },
  '&:active': {
    opacity: '0.8'
  },
  '&:focus-visible': {
    boxShadow: '$outline'
  },
  variants: {
    size: {
      small: {
        fontSize: '$bodySmall',
        padding: '$2 $3',
        height: 32
      },
      standard: {
        fontSize: '$bodySmall',
        padding: '$4',
        height: 48
      },
      large: {
        fontSize: '$bodyLarge',
        padding: '0 $6',
        height: 64
      }
    },
    shape: {
      sharp: {
        borderRadius: 0
      },
      small: {
        borderRadius: '$small'
      },
      medium: {
        borderRadius: '$medium'
      },
      large: {
        borderRadius: '$large'
      },
      pill: {
        borderRadius: '$pill'
      }
    },
    variant: {
      standard: {
        background: '$actionableDefault',
        color: '$actionableContent',
        '&:hover': {
          background: '$actionableHover'
        }
      },
      alt: {
        background: '$actionableAlt',
        color: '$actionableAltContent',
        '&:disabled': {
          background: '$disabledAltBackground',
          color: '$disabledAltContent'
        },
        '&:hover': {
          background: '$actionableAltHover'
        }
      },
      conversion: {
        background: '$conversionButtonDefault',
        color: '$conversionButtonContent',
        '&:hover': {
          background: '$conversionButtonHover'
        }
      },
      warning: {
        background: '$error-standard',
        color: '$actionableContent',
        '&:hover': {
          background: actionNavigationWarningHighlight
        }
      },
      success: {
        background: '$success-standard',
        color: '$backgroundPrimary',
        '&:hover': {
          background: '$actionSuccessHighlight'
        }
      },
      alert: {
        background: '$alert-standard',
        color: '$actionableContent',
        '&:hover': {
          background: '$actionAlertHighlight'
        }
      },
      ghost: {
        background: 'transparent',
        boxShadow: 'inset 0 0 0 1px $colors$actionableDefault',
        color: '$actionableDefault',
        '&:hover': {
          background: '$actionableHover',
          boxShadow: 'inset 0 0 0 1px $colors$actionableHover',
          color: '$backgroundPrimary'
        },
        '&:disabled': {
          boxShadow: 'none'
        }
      },
      'ghost-alt': {
        background: 'transparent',
        boxShadow: 'inset 0 0 0 1px $colors$actionableAlt',
        color: '$actionableAlt',
        '&:hover': {
          background: '$actionableAlt',
          color: '$actionableHover'
        }
      },
      unstyled: {
        padding: 0,
        background: 'transparent',
        color: 'inherit'
      }
    },
    isFull: {
      true: {
        width: '100%'
      }
    },
    isUppercase: {
      true: {
        textTransform: 'uppercase'
      }
    },
    hasLeftIcon: {
      true: {
        paddingRight: '$5'
      }
    },
    hasRightIcon: {
      true: {
        paddingLeft: '$5'
      }
    }
  },
  defaultVariants: {
    variant: 'standard',
    size: 'standard',
    shape: 'small'
  }
});
/**
 * @deprecated Esta tipagem refere-se a um componente que será descontinuado em futuras Breaking Changes do Flora.
 * @tutorial
 * {@link https://florabook.gb.tech/v2/index.html?path=/docs/core-components-florabutton-guia-de-migra%C3%A7%C3%A3o--page| Acesse o guia de migração} para saber como migrar para a nova versão.
 */

var needMargin = need => need ? 8 : 0;
/**
 * @deprecated Este componente será descontinuado em futuras Breaking Changes do Flora.
 * @tutorial
 * {@link https://florabook.gb.tech/v2/index.html?path=/docs/core-components-florabutton-guia-de-migra%C3%A7%C3%A3o--page| Acesse o guia de migração} para saber como migrar para a nova versão.
 */


export var Button = forwardRef((_ref, forwardedRef) => {
  var {
    children,
    leftIcon,
    rightIcon,
    label = '',
    disabled,
    isLoading,
    loadingText
  } = _ref,
      props = _objectWithoutPropertiesLoose(_ref, _excluded);

  useEffect(() => {
    if ((leftIcon || rightIcon) && !children && !(label || props['aria-label'])) {
      throw new Error('you must use the label when passing an icon');
    }
  }, [leftIcon, rightIcon, children, label]);
  var isDisabled = disabled || isLoading;
  var spinnerSize = props.size === 'small' ? 'xxs' : 'xs';
  return /*#__PURE__*/React.createElement(StyledButton, _extends({
    ref: forwardedRef,
    disabled: isDisabled,
    "aria-disabled": isDisabled,
    "data-flora": "button",
    "data-flora-text": typeof children === 'string' ? children : label
  }, props, {
    hasLeftIcon: !!leftIcon,
    hasRightIcon: !!rightIcon,
    css: _extends({}, props.css, {
      'svg, [data-flora-spinner]': {
        marginLeft: !children ? 0 : needMargin(!!rightIcon),
        marginRight: !children ? 0 : needMargin(!!leftIcon || !!isLoading)
      }
    })
  }), leftIcon && !isLoading && /*#__PURE__*/React.createElement(AccessibleIcon, {
    label: label
  }, leftIcon), isLoading ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Spinner, {
    size: spinnerSize
  }), loadingText || children) : children, rightIcon && !isLoading && /*#__PURE__*/React.createElement(AccessibleIcon, {
    label: label
  }, rightIcon));
});
//# sourceMappingURL=Button.js.map